import SearchIcon from '@mui/icons-material/Search';
import { Button, Chip, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import type { RegistryEntitySummary } from '../../api/registryEntityTypes';
import {
  ENTITY_TYPE_LABELS,
  formatAmount,
  formatBusinessNumber,
  isJointHolding,
} from '../../entities/entityFormat';
import { entityPath, SAMPLE_HOLDERS_PATH } from '../../entities/entityPaths';
import { useEntitySource } from '../../entities/useEntitySource';
import { useSourceQuery } from '../../entities/useSourceQuery';
import DataTable, { type Column } from '../../ui/DataTable';
import { PageHeader, StatusChip } from '../../ui/primitives';
import { ENTITY_FILTERS, filterEntities } from './entityFilters';
import SourceNotice, { QueryStatus } from './SourceNotice';

function businessNumbers(entity: RegistryEntitySummary): string {
  const parts = [
    entity.abn && `ABN ${formatBusinessNumber(entity.abn)}`,
    entity.acn && `ACN ${formatBusinessNumber(entity.acn)}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

const ENTITY_COLUMNS: Column<RegistryEntitySummary>[] = [
  {
    key: 'name',
    label: 'Investment entity',
    sortValue: (row) => row.name.toLowerCase(),
    render: (row) => (
      <>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ENTITY_TYPE_LABELS[row.type]}
        </Typography>
      </>
    ),
  },
  {
    key: 'numbers',
    label: 'ABN / ACN',
    hideBelow: 'md',
    render: businessNumbers,
  },
  {
    key: 'owners',
    label: 'Contacts',
    sortValue: (row) => row.ownerCount,
    render: (row) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <span>
          {row.ownerCount} owner{row.ownerCount === 1 ? '' : 's'}
          {row.representativeCount ? ` · ${row.representativeCount} rep.` : ''}
        </span>
        {isJointHolding(row.ownerCount) && <StatusChip status="OPEN" label="Joint" />}
      </Stack>
    ),
  },
  {
    key: 'allocated',
    label: 'Allocated',
    align: 'right',
    sortValue: (row) => row.allocationCount,
    render: (row) =>
      row.allocationCount ? `${formatAmount(row.totalAllocated)} (${row.allocationCount})` : '—',
  },
  {
    key: 'foreign',
    label: 'Foreign',
    hideBelow: 'lg',
    render: (row) => (row.isForeignInvestor ? 'Yes' : '—'),
  },
];

function EntityFilterBar({
  entities,
  filter,
  onFilter,
  search,
  onSearch,
}: {
  entities: RegistryEntitySummary[];
  filter: string;
  onFilter: (key: string) => void;
  search: string;
  onSearch: (value: string) => void;
}) {
  return (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      <TextField
        size="small"
        placeholder="Search by name, ABN or ACN"
        inputProps={{ 'aria-label': 'Search entities' }}
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {ENTITY_FILTERS.map((item) => (
          <Chip
            key={item.key}
            label={`${item.label} (${entities.filter(item.test).length})`}
            color={filter === item.key ? 'primary' : 'default'}
            variant={filter === item.key ? 'filled' : 'outlined'}
            onClick={() => onFilter(item.key)}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function EntityTable({ entities }: { entities: RegistryEntitySummary[] }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') ?? 'all';
  const [search, setSearch] = useState('');
  const rows = useMemo(() => filterEntities(entities, filter, search), [entities, filter, search]);
  return (
    <Paper variant="outlined">
      <EntityFilterBar
        entities={entities}
        filter={filter}
        onFilter={(key) => setParams(key === 'all' ? {} : { filter: key })}
        search={search}
        onSearch={setSearch}
      />
      <DataTable
        rows={rows}
        rowKey={(row) => row.externalId}
        onRowClick={(row) => navigate(entityPath(row.externalId))}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="No investment entities match"
        dense
        columns={ENTITY_COLUMNS}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 2 }}>
        Showing {rows.length} of {entities.length} investment entities.
      </Typography>
    </Paper>
  );
}

/** Holders are the platform's investment entities: real from the admin API when signed in. */
export default function EntitiesPage() {
  const source = useEntitySource();
  const load = useCallback(() => source.listEntities(), [source]);
  const query = useSourceQuery(load);
  return (
    <>
      <PageHeader
        title="Holders"
        subtitle="Every holder is an investment entity on the Fides platform. A joint holding is one entity with two or more Owner contacts."
        actions={
          <Button component={RouterLink} to={SAMPLE_HOLDERS_PATH} variant="outlined">
            Sample registry records
          </Button>
        }
      />
      <SourceNotice sample={source.sample} />
      <QueryStatus query={query}>{(entities) => <EntityTable entities={entities} />}</QueryStatus>
    </>
  );
}
